import dayjs from "@dentalai/dayjs";
import {
  useEmbedNonStylesConfig,
  useEmbedStyles,
  useIsBackgroundTransparent,
  useIsEmbed,
} from "@dentalai/embed-core/embed-iframe";
import CustomBranding from "@dentalai/lib/CustomBranding";
import classNames from "@dentalai/lib/classNames";
import useTheme from "@dentalai/lib/hooks/useTheme";
import { detectBrowserTimeFormat, setIs24hClockInLocalStorage, TimeFormat } from "@dentalai/lib/timeFormat";
import { Button, Form, TextField } from "@dentalai/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import type { Provider, Operatory, ReasonForVisit, VisitCategory } from "@prisma/client";
import { PatientStatus } from "@prisma/client";
import dynamic from "next/dynamic";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { valueContainerCSS } from "react-select/dist/declarations/src/components/containers";
import { z } from "zod";

import { timeZone as localStorageTimeZone } from "@lib/clock";
import { SpecialityOptions } from "@lib/constant";
import useRouterQuery from "@lib/hooks/useRouterQuery";
import { checkRealTimeAvailability, searchPatient } from "@lib/practice";
import { formatDate } from "@lib/practice/helper";

import type { AvailabilityPageProps } from "../../../pages/[user]/[type]";
import UserAvatar from "../../../public/user.png";

type PatientFormValues = {
  firstName: string;
  lastName: string;
  birthdate: string;
};

interface AgeRangeInput {
  age: number;
  minAge?: number | null;
  maxAge?: number | null;
}

type CategoryGroup = {
  label: string;
  types: AppointmentType[];
};

const ageIsInRange = ({ age, minAge, maxAge }: AgeRangeInput) => {
  if (minAge && maxAge) {
    return age >= minAge && age <= maxAge;
  } else if (minAge) {
    return age >= minAge;
  } else if (maxAge) {
    return age <= maxAge;
  } else {
    return true;
  }
};

const Toaster = dynamic(() => import("react-hot-toast").then((mod) => mod.Toaster), { ssr: false });
const SlotPicker = dynamic(() => import("../SlotPicker").then((mod) => mod.SlotPicker), {
  ssr: false,
  loading: () => <div className="mt-8 px-4 pb-4 sm:mt-0 sm:p-4 md:min-w-[300px] md:px-5 lg:min-w-[455px]" />,
});

type AppointmentType = ReasonForVisit & { providers: AppointmentProvider[]; visitCategory: VisitCategory };
type AppointmentProvider = Provider & { operatories: Operatory[] };

const AvailabilityPage = ({ profile, eventType, appointmentTypes, ...restProps }: AvailabilityPageProps) => {
  //Snack Bars
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const router = useRouter();
  const isEmbed = useIsEmbed(restProps.isEmbed);
  useTheme(profile.theme);
  const availabilityDatePickerEmbedStyles = useEmbedStyles("availabilityDatePicker");
  //TODO: Plan to remove shouldAlignCentrallyInEmbed config
  const shouldAlignCentrallyInEmbed = useEmbedNonStylesConfig("align") !== "left";
  const shouldAlignCentrally = !isEmbed || shouldAlignCentrallyInEmbed;
  const isBackgroundTransparent = useIsBackgroundTransparent();

  const { time } = useRouterQuery("time");
  const [page, setPage] = useState(0);
  const [timeZone, setTimeZone] = useState<string>();
  const [timeFormat, setTimeFormat] = useState<TimeFormat>(detectBrowserTimeFormat);
  const [newPatient, setPatientStatus] = useState<boolean>(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [birthday, setBirthday] = useState("");
  const [filteredTypes, setFilteredTypes] = useState<Array<AppointmentType>>([]);
  const [groupedTypes, setGroupedTypes] = useState<Record<string, CategoryGroup>>({});
  const [currentType, setCurrentType] = useState<AppointmentType>();
  const [currentProvider, setCurrentProvider] = useState<Provider>();
  const [operatories, setOperatories] = useState<Array<number>>([]);
  const [includeBlockouts, setIncludeBlockouts] = useState<Array<number>>([]);
  const [excludeBlockouts, setExcludeBlockouts] = useState<Array<number>>([]);
  const [days, setDays] = useState<Array<number>>([]);
  const [patientSearch, setPatientSearch] = useState(false);
  const [dateSlotLoading, setDateSlotLoading] = useState(true);
  const [timeSlotLoading, setTimeSlotLoading] = useState(true);

  const onTimeFormatChange = (is24Hours: boolean) => {
    setTimeFormat(is24Hours ? TimeFormat.TWENTY_FOUR_HOUR : TimeFormat.TWELVE_HOUR);
    setIs24hClockInLocalStorage(is24Hours);
  };

  useEffect(() => {
    setTimeZone(localStorageTimeZone() || dayjs.tz.guess());
  }, []);

  const groupTypes = (types: AppointmentType[]) => {
    const groups: Record<string, CategoryGroup> = {};

    types.forEach((type) => {
      const category = type.visitCategory?.name !== undefined ? type.visitCategory.name : "z";
      groups[category] = groups[category] || { label: "", types: [] };
      groups[category].label = type.visitCategory?.name || "z";
      groups[category].types.push(type);
    });

    setGroupedTypes(groups);
  };

  useEffect(() => {
    if (appointmentTypes) {
      const patientStatus = newPatient ? PatientStatus.NEW : PatientStatus.RETURNING;
      const types = appointmentTypes.filter((apptType) => {
        const age = dayjs().diff(dayjs(birthday), "year");
        // return apptType.patientStatus == patientStatus && apptType.lowerAgeLimit <= age;
        return (
          apptType.patientStatus == patientStatus &&
          ageIsInRange({ age, minAge: apptType.minAge, maxAge: apptType.maxAge })
        );
      });
      setFilteredTypes(types);
      groupTypes(types);
    }
  }, [appointmentTypes, birthday, newPatient]);

  const patientForm = useForm<PatientFormValues>({
    resolver: zodResolver(
      z.object({
        firstName: z.string().min(1),
        lastName: z.string().min(1),
        birthdate: z
          .string()
          .refine((value) => dayjs(value, "MM/DD/YYYY", true).isValid(), "Birthday is not valid"),
      })
    ),
  });

  const { register } = patientForm;

  const getPatientInfo = async (values: PatientFormValues) => {
    const params = {
      ...values,
      birthday: dayjs(values.birthdate, "MM/DD/YYYY").format("YYYY-MM-DD"),
      userName: profile?.username,
    };

    setPatientSearch(true);
    const patientList = await searchPatient(params);
    setPatientStatus(patientList.length < 1);
    setPatientSearch(false);

    if (patientList.length > 0) {
      setPhone(
        patientList[0].phone ||
          patientList[0].cellPhone ||
          patientList[0].homePhone ||
          patientList[0].workPhone
      );
      setEmail(patientList[0].email);
    } else {
      setPhone("");
      setEmail("");
    }

    setPage(1);
    setFirstName(values.firstName);
    setLastName(values.lastName);
    setBirthday(values.birthdate);
  };

  const setAppointmentType = (type: AppointmentType) => {
    setCurrentType(type);
    setIncludeBlockouts(type.includeBlockouts);
    setExcludeBlockouts(type.excludeBlockouts);
  };

  const selectProvider = (provider: AppointmentProvider) => {
    setCurrentProvider(provider);
    setOperatories(provider.operatories.map((op) => op.opNum));
    setDays(provider.availableDays);
  };

  const checkIsCollisionFree = async () => {
    if (!profile.username) {
      return false;
    }
    const newApptStartTime = router.query.time as string;
    const newApptduration = currentType?.duration || 60;
    const operatoryId = Number(router.query.op);
    const userName = profile.username || "";

    const response = await checkRealTimeAvailability({
      newApptStartTime,
      newApptduration,
      operatoryId,
      userName,
    });

    return response.collisionFree;
  };
  const [checkingCollision, setCheckingCollision] = useState(false);
  const navigateBookingPage = async () => {
    setCheckingCollision(true);
    const isCollisionFree = await checkIsCollisionFree();
    if (!isCollisionFree) {
      enqueueSnackbar("This appointment slot was just taken.", {
        variant: "error",
        style: { backgroundColor: "red" },
        autoHideDuration: 1000,
      });
      setCheckingCollision(false);
      // setCreating(false);
      console.log("Collision Found");
      return;
    }
    setCheckingCollision(false);
    console.log("Navigating...");
    router.push({
      pathname: "book",
      query: {
        ...router.query,
        firstName,
        lastName,
        birthday,
        email,
        phone,
        reason: currentType?.id,
        provider: currentProvider?.id,
        status: newPatient ? PatientStatus.NEW : PatientStatus.RETURNING,
        type: eventType.id,
        slug: eventType.slug,
      },
    });
  };

  return (
    <div>
      <Head>
        <title>
          {eventType.title} | {profile.name}
        </title>
      </Head>
      <CustomBranding lightVal={profile.brandColor} darkVal={profile.darkBrandColor} />
      <div>
        <main
          className={classNames(
            "flex flex-col md:mx-4",
            shouldAlignCentrally ? "items-center" : "items-start",
            !isEmbed && classNames("mx-auto my-0 ease-in-out md:my-24")
          )}>
          <div className="border-gray-200">
            <div className="bg-brand flex flex-row items-center rounded-tl-md rounded-tr-md p-4">
              <div className="mx-4 w-48 rounded bg-white py-1.5">
                <img src={profile.logo || ""} alt="" className="mx-auto h-14 w-auto" />
              </div>
              <p className="font-bold text-white">{profile.name}</p>
            </div>
            <div
              style={availabilityDatePickerEmbedStyles}
              className={classNames(
                isBackgroundTransparent ? "" : "flex flex-col bg-white px-8 py-4 md:px-16 md:py-8",
                "border-bookinglightest md:rounded-md md:border",
                isEmbed && "mx-auto"
              )}>
              <div>
                <h2 className="mb-2 text-lg font-bold text-gray-800 md:text-2xl">
                  {page == 0 ? "Who would you like to schedule an appointment for?" : "Schedule Appointment"}
                </h2>
              </div>
              <div className="mt-2 mb-2 md:mt-8 md:mb-6 md:min-w-[39rem]">
                {page == 0 && (
                  <Form form={patientForm} handleSubmit={getPatientInfo}>
                    <div className="mb-4">
                      <p className="mb-2 text-sm font-medium text-gray-600">Patient Name</p>
                      <div className="flex flex-row gap-4">
                        <TextField
                          containerClassName="flex-1"
                          className="w-full"
                          label=""
                          placeholder="First Name"
                          {...register("firstName")}
                          onChange={(event) => {
                            patientForm.setValue("firstName", event.target.value.trim());
                          }}
                        />
                        <TextField
                          containerClassName="flex-1"
                          className="w-full"
                          label=""
                          placeholder="Last Name"
                          {...register("lastName")}
                          onChange={(event) => {
                            patientForm.setValue("lastName", event.target.value.trim());
                          }}
                        />
                      </div>

                      <p className="mt-4 text-sm font-medium text-gray-600">Date of Birth</p>
                      <div className="flex">
                        <TextField
                          containerClassName="flex-1"
                          className="w-full"
                          label=""
                          placeholder="MM/DD/YYYY"
                          {...register("birthdate")}
                          onChange={(event) => {
                            patientForm.setValue("birthdate", formatDate(event.target.value));
                          }}
                        />
                      </div>
                    </div>

                    <div className="border-bookinglightest border-b pb-4" />
                    <div className="flex flex-row justify-end">
                      <Button
                        className="bg-brand mt-4 h-10 w-28 justify-center text-xs font-bold uppercase disabled:bg-gray-400 md:mt-6 md:w-56 md:text-sm"
                        loading={patientSearch}
                        type="submit">
                        <p>Next</p>
                      </Button>
                    </div>
                  </Form>
                )}
                {page == 1 && (
                  <div>
                    {filteredTypes.length > 0 && (
                      <p className="mb-2 text-sm font-medium text-gray-600">What can we help you with?</p>
                    )}
                    <div
                      className={classNames(
                        "border-bookinglightest relative border-b pb-4",
                        filteredTypes.length ? "" : "min-h-40"
                      )}>
                      {!filteredTypes.length && (
                        <div className="absolute top-1/2 left-1/2 w-full -translate-x-1/2 -translate-y-1/2 space-y-2">
                          <p className="text-center text-xl font-medium">
                            There is no appointment type suitable for you.
                          </p>
                          <p className="text-center text-xl font-medium">
                            Please call our office to book an appointment.
                          </p>
                        </div>
                      )}
                      {Object.entries(groupedTypes)
                        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                        .map(([key, value]) => (
                          <>
                            {(Object.keys(groupedTypes).length > 1 ||
                              Object.keys(groupedTypes)[0] != "z") && (
                              <h2 className="title py-1 text-sm font-medium text-teal-700">
                                {key !== "z" ? key : "Other"}
                              </h2>
                            )}
                            <div key={key} className="grid grid-cols-2 gap-2">
                              {value.types.map((type, index) => {
                                const selected = type.id === currentType?.id;
                                return (
                                  <div
                                    key={index}
                                    onClick={() => setAppointmentType(type)}
                                    className={classNames(
                                      "border-bookinglightest hover:bg-brand my-1 cursor-pointer rounded-lg border p-3 hover:text-white",
                                      selected ? "bg-brand text-white" : ""
                                    )}>
                                    <p className="text-center">{type.name}</p>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ))}
                    </div>
                    <div className="mt-6 flex flex-row justify-between">
                      <Button
                        className="hover:bg-brand h-10 w-28 justify-center border border-black bg-white text-xs font-bold uppercase text-gray-800 hover:border-0 hover:text-white md:w-56 md:text-sm"
                        onClick={() => setPage(0)}>
                        <p>Back</p>
                      </Button>
                      <Button
                        className="hover:bg-brand bg-brand h-10 w-28 justify-center text-xs font-bold uppercase disabled:bg-gray-400 md:w-56 md:text-sm"
                        disabled={!currentType?.id}
                        onClick={() => setPage(2)}>
                        <p>Next</p>
                      </Button>
                    </div>
                  </div>
                )}
                {page == 2 && (
                  <div>
                    <h3 className="mb-4 text-lg font-bold text-gray-700">
                      Which provider would you like see?
                    </h3>
                    <div className="border-bookinglightest grid grid-cols-2 gap-4 border-b pb-6">
                      {currentType?.providers.map((provider, index) => {
                        const selected = provider.id === currentProvider?.id;
                        const speciality = SpecialityOptions.find(
                          (option) => option.value == provider.speciality
                        );

                        return (
                          <div
                            key={index}
                            onClick={() => selectProvider(provider)}
                            className={classNames(
                              "border-bookinglightest flex cursor-pointer flex-row items-center gap-2 rounded-lg border p-2",
                              selected ? "bg-brand text-white" : ""
                            )}>
                            <Image
                              src={provider.avatar || UserAvatar}
                              alt=""
                              width={100}
                              height={100}
                              className="mr-4 h-12 w-12 rounded-full"
                            />
                            <div>
                              <p className="font-bold">{provider.name}</p>
                              <p className="text-sm">{speciality?.label}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-6 flex flex-row justify-between">
                      <Button
                        className="hover:bg-brand h-10 w-28 justify-center border border-black bg-white text-xs font-bold uppercase text-gray-800 hover:border-0 hover:text-white md:w-56 md:text-sm"
                        onClick={() => setPage(1)}>
                        <p>Back</p>
                      </Button>
                      <Button
                        className="hover:bg-brand bg-brand h-10 w-28 justify-center text-xs font-bold uppercase disabled:bg-gray-400 md:w-56 md:text-sm"
                        disabled={!currentProvider?.id}
                        onClick={() => setPage(3)}>
                        <p>Next</p>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {page == 3 && (
                <div>
                  <div className="md:flex">
                    <SlotPicker
                      weekStart={
                        typeof profile.weekStart === "string"
                          ? ([
                              "Sunday",
                              "Monday",
                              "Tuesday",
                              "Wednesday",
                              "Thursday",
                              "Friday",
                              "Saturday",
                            ].indexOf(profile.weekStart) as 0 | 1 | 2 | 3 | 4 | 5 | 6)
                          : profile.weekStart /* Allows providing weekStart as number */
                      }
                      eventType={eventType}
                      timeFormat={timeFormat}
                      onTimeFormatChange={onTimeFormatChange}
                      timeZone={timeZone}
                      provider={currentProvider?.provNum || 0}
                      operatories={operatories}
                      includeBlockouts={includeBlockouts}
                      excludeBlockouts={excludeBlockouts}
                      availableDays={days}
                      duration={currentType?.duration || 60}
                      userName={profile.username || ""}
                      dateSlotLoading={dateSlotLoading}
                      setDateSlotLoading={setDateSlotLoading}
                      timeSlotLoading={timeSlotLoading}
                      setTimeSlotLoading={setTimeSlotLoading}
                    />
                  </div>
                  <div className="mt-4 flex flex-row justify-between md:mt-0">
                    <Button
                      className="hover:bg-brand h-10 w-28 justify-center border border-black bg-white text-xs font-bold uppercase text-gray-800 hover:border-0 hover:text-white md:w-56 md:text-sm"
                      onClick={() => setPage(2)}>
                      <p>Back</p>
                    </Button>
                    <Button
                      className="hover:bg-brand h-10 w-28 justify-center border border-black bg-white text-xs font-bold uppercase text-gray-800 hover:border-0 hover:text-white md:w-56 md:text-sm"
                      disabled={!time}
                      loading={checkingCollision}
                      onClick={navigateBookingPage}>
                      <p>Next</p>
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {/* FIXME: We don't show branding in Embed yet because we need to place branding on top of the main content. Keeping it outside the main content would have visibility issues because outside main content background is transparent */}
            {/* {!restProps.isBrandingHidden && !isEmbed && <PoweredByCal />}*/}
          </div>
          <p className="mt-8 text-center text-sm text-gray-600 hover:text-gray-900 md:text-base">
            Powered by Dental AI
          </p>
        </main>
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
};

export default AvailabilityPage;
